import * as yup from 'yup'

import {
  FormSchema,
} from '../../types/form'

import {
  formSchema as crmSchema,
  emptyValues as crmValues,
} from '../crm/forms'

import {
  getInitialValues,
} from '@kaidam/fc-shared/src/frontend/components/form/utils'

import {
  splitCoverageString,
  checkCoverageOutcode,
} from '@kaidam/fc-shared/src/booking/utils'

import {
  mapSchema,
} from '@kaidam/fc-shared/src/utils/form'

import {
  IRecord_Booking_Core_Editable,
} from '@kaidam/fc-shared/src/types/schema/booking'

import {
  BOOKING_STATE_CONFIRMED,
} from '@kaidam/fc-shared/src/constants'

yup.addMethod(yup.string, 'coverageOutcodes', function (errorMessage) {
  return this.test(`coverage-outcodes`, errorMessage, function (value) {
    const { path, createError } = this
    const parts = splitCoverageString(value)
    const invalidOutcode = parts.find(part => !checkCoverageOutcode(part))
    if(!invalidOutcode) return true
    return createError({ path, message: `${invalidOutcode} ${errorMessage}` })
  })
})

export const crmValidation = yup.object().shape({
  firstname: yup.string().nullable().required(),
  surname: yup.string().nullable(),
  email: yup.string().email().required(),
  phone_number: yup.string().nullable(),
  phone_number2: yup.string().nullable(),
  address1: yup.string().nullable().required(),
  address2: yup.string().nullable(),
  city: yup.string().nullable(),
  postcode: yup.string().nullable().required(),
  country: yup.string().nullable(),
})

/*

  UTILS

*/
export const prependFormSchema = (schema: FormSchema, prepend: string) => {
  return mapSchema(schema, field => {
    return Object.assign({}, field, {
      id: `${prepend}${field.id}`
    })
  })
}

/*

  ENERGY USAGE

*/
export const energyUsageSchema: any = [{
  id: 'gasUseHeading',
  title: 'Gas Use',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'annualGasUse',
  title: 'Annual gas use in kWh',
  component: 'text',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'gasUseEstimated',
  title: 'Is the value based on estimated use? (tick for yes)',
  component: 'checkbox',
  meta: {
    notitle: true,
  }
}, {
  id: 'gasUseStartDate',
  title: 'Gas use start date',
  component: 'text',
  inputProps: {
    type: 'date',
  },
  default: '2020-01-01',
}, {
  id: 'gasUseEndDate',
  title: 'Gas use end date',
  component: 'text',
  inputProps: {
    type: 'date',
  },
  default: '2020-01-01',
}, {
  id: 'electricityUseHeading',
  title: 'Electricity Use',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'annualElectricityUse',
  title: 'Annual electricity use in kWh',
  component: 'text',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'electricityUseEstimated',
  title: 'Is the value based on estimated use? (tick for yes)',
  component: 'checkbox',
  meta: {
    notitle: true,
  }
}, {
  id: 'electricityUseStartDate',
  title: 'Electricity use start date',
  component: 'text',
  inputProps: {
    type: 'date',
  },
  default: '2020-01-01',
}, {
  id: 'electricityUseEndDate',
  title: 'Electricity use end date',
  component: 'text',
  inputProps: {
    type: 'date',
  },
  default: '2020-01-01',
}, {
  id: 'otherUseHeading',
  title: 'Other energy Use',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'annualSolidUse',
  title: 'Annual spend on solid fuel in £',
  component: 'text',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'annualRenewableUse',
  title: 'Annual contribution from renewable generation in kWh',
  component: 'text',
  inputProps: {
    type: 'number',
  }
}]

/*

  PRE SURVEY

*/
export const prebookingSchema: FormSchema = [{
  id: 'organiseSectionHeading',
  title: 'Details to organise your survey',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'specialRequirementsHelperList',
  component: 'html',
  helperText: `
<p style="margin-top:20px;">Do you have any special requirements that we need to be aware of during the survey?</p>
<ul style="color:#999;">
  <li>Do you have any illnesses or disabilities?</li>
  <li>Will you be able to accompany the surveyor around your home for two to three hours during the survey?</li>
  <li>Do you have any pets that will be present in your home during the survey?</li>
  <li>What is the parking situation like? Do you have spare permits?</li>
</ul>
`,
  cosmetic: true,
}, {
  id: 'specialRequirements',
  title: 'Please enter any special requirements here:',
  component: 'textarea',
}, {
  id: 'asbestosInBuilding',
  title: 'Are you aware of any asbestos, exposed or not exposed, in the building? (tick for yes)',
  component: 'checkbox',
  meta: {
    notitle: true,
  }
}, {
  id: 'asbestosInBuildingDetails',
  title: 'If yes, please give details',
  component: 'textarea',
}, {  
  id: 'otherSectionHeading',
  title: 'Other details',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'canHeatHome',
  title: 'Can you heat your home to a comfortable temperature in the winter? (tick for yes)',
  component: 'checkbox',
  meta: {
    notitle: true,
  }
}, {
  id: 'howBenefitFromSurvey',
  title: 'How do you think you will benefit from a survey?',
  component: 'textarea',
}]

// if the booking is > 14 days away - we don't display the cancel check box
export const prebookingValidationFarAway = yup.object().shape({
  conditions_photos: yup.bool().oneOf([true], 'field must be checked'),
  conditions_prep: yup.bool().oneOf([true], 'field must be checked'),
})

// if the booking is <= 14 days away - we display the cancel check box
export const prebookingValidationClose = yup.object().shape({
  conditions_photos: yup.bool().oneOf([true], 'field must be checked'),
  conditions_prep: yup.bool().oneOf([true], 'field must be checked'),
  conditions_cancel: yup.bool().oneOf([true], 'field must be checked'),
})

/*

  PRE SURVEY

*/
export const presurveySchema = [{
  id: 'nextSectionHeading',
  title: 'Details of your home',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'homeDetails',
  title: 'Building Type (*)',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
      title: 'Bungalow',
      value: 'Bungalow',
    }, {
      title: 'Detached',
      value: 'Detached',
    }, {
      title: 'End terrace',
      value: 'End terrace',
    }, {
      title: 'Flat',
      value: 'Flat',
    }, {
      title: 'Maisonette',  
      value: 'Maisonette',
    }, {
      title: 'Mid terrace',   
      value: 'Mid terrace',
    }, {
      title: 'Other',     
      value: 'Other',
    }, {
      title: 'Semi-detached', 
      value: 'Semi-detached',
    }, {
      title: 'Unknown', 
      value: 'Unknown',
    },
  ],
}, {
  id: 'bedroomCount',
  title: 'How many bedrooms?',
  helperText: 'If you have more than 3 bedrooms please answer the next question also',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
      title: '1',
      value: '1',
    }, {
      title: '2',
      value: '2',
    }, {
      title: '3',
      value: '3',
    }, {
      title: 'More than 3',
      value: '3plus',
    }
  ],
}, {
  id: 'totalRooms',
  title: 'How many rooms in total?',
  helperText: 'Excluding hallways and unheated rooms/spaces',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
      title: '1',
      value: '1',
    }, {
      title: '2',
      value: '2',
    }, {
      title: '3',
      value: '3',
    }, {
      title: '4',
      value: '4',
    }, {
      title: '5',
      value: '5',
    }, {
      title: '6',
      value: '6',
    }, {
      title: '7',
      value: '7',
    }, {
      title: '8',
      value: '8',
    }, {
      title: '9',
      value: '9',
    }, {
      title: '10',
      value: '10',
    }
  ],
}, {
  id: 'constructionYear',
  title: 'Year of Construction',
}, {
  id: 'wallConstruction',
  title: 'Wall Construction',
  component: 'select',
  options: [{
    title: 'Please select...',
    value: '',
    }, {
    title: 'Insulated cavity',
    value: 'Insulated cavity',
    }, {
    title: 'Other',
    value: 'Other',
    }, {
    title: 'Solid brick',
    value: 'Solid brick',
    }, {
    title: 'Stone',
    value: 'Stone',
    }, {
    title: 'Timber',
    value: 'Timber',
    }, {
    title: 'Uninsulated Cavity',
    value: 'Uninsulated cavity',
    }, {
    title: 'Unknown',
    value: 'Unknown',
    },
  ],
}, {
  id: 'occupation',
  title: 'Occupation Type',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
      title: 'Other',
      value: 'Other',
    }, {
      title: 'Rented council',
      value: 'Rented council',
    }, {
      title: 'Rented housing association',
      value: 'Rented housing association',
    }, {
      title: 'Rented privately',
      value: 'Rented privately',
    }, {
      title: 'Owner Occupied',
      value: 'Owner Occupied',
    }, {
      title: 'Unknown', 
      value: 'Unknown',
    },
  ],
}, {
  id: 'energySupplier',
  title: 'Energy Supplier',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
      title: 'Bristol Energy',
      value: 'Bristol Energy',
    }, {
      title: 'British Gas',
      value: 'British Gas',
    }, {
      title: 'Ecotricity',
      value: 'Ecotricity',
    }, {
      title: 'EDF',
      value: 'EDF',
    }, {
      title: 'EON',
      value: 'EON',
    }, {
      title: 'Good Energy',
      value: 'Good Energy',
    }, {
      title: 'Npower',
      value: 'Npower',
    }, {
      title: 'Octopus',
      value: 'Octopus',
    }, {
      title: 'Opus',
      value: 'Opus',
    }, {
      title: 'Ovo',
      value: 'Ovo',
    }, {
      title: 'Scottish Power',
      value: 'Scottish Power',
    }, {
      title: 'SSE',
      value: 'SSE',
    }, {
      title: 'Other', 
      value: 'Other',
    },
  ],
}, {
  id: 'primaryHeating',
  title: 'Primary Heating Type',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
        title: 'AGA or similar (electric)',
        value: 'AGA or similar (electric)',
    }, {
        title: 'AGA or similar (gas)',
        value: 'AGA or similar (gas)',
    }, {
        title: 'Central heating (electric)',
        value: 'Central heating (electric)',
    }, {
        title: 'Central heating (gas)',
        value: 'Central heating (gas)',
    }, {
        title: 'Electric heaters',
        value: 'Electric heaters',
    }, {
        title: 'Electric storage heaters',
        value: 'Electric storage heaters',
    }, {
      title: 'Heat Pumps',
      value: 'Heat Pumps',
    }, {
      title: 'Oil',
      value: 'Oil',
    }, {
        title: 'Gas fire',
        value: 'Gas fire',
    }, {
        title: 'None',
        value: 'None',
    }, {
        title: 'Open fire',
        value: 'Open fire',
    }, {
        title: 'Other',
        value: 'Other',
    }, {
        title: 'Underfloor (electric)',
        value: 'Underfloor (electric)',
    }, {
        title: 'Underfloor (gas)',
        value: 'Underfloor (gas)',
    }, {
        title: 'Underfloor (solar)',
        value: 'Underfloor (solar)',
    }, {
        title: 'Unknown',
        value:'Unknown',
    }, {
        title: 'Wood burner',
        value: 'Wood burner',
    },
  ],
}, {
  id: 'secondaryHeating',
  title: 'Secondary Heating Type',
  component: 'select',
  options: [{
      title: 'Please select...',
      value: '',
    }, {
        title: 'AGA or similar (electric)',
        value: 'AGA or similar (electric)',
    }, {
        title: 'AGA or similar (gas)',
        value: 'AGA or similar (gas)',
    }, {
        title: 'Central heating (electric)',
        value: 'Central heating (electric)',
    }, {
        title: 'Central heating (gas)',
        value: 'Central heating (gas)',
    }, {
        title: 'Electric heaters',
        value: 'Electric heaters',
    }, {
        title: 'Electric storage heaters',
        value: 'Electric storage heaters',
    }, {
      title: 'Oil',
      value: 'Oil',
    }, {
        title: 'Gas fire',
        value: 'Gas fire',
    }, {
        title: 'None',
        value: 'None',
    }, {
        title: 'Open fire',
        value: 'Open fire',
    }, {
        title: 'Other',
        value: 'Other',
    }, {
        title: 'Underfloor (electric)',
        value: 'Underfloor (electric)',
    }, {
        title: 'Underfloor (gas)',
        value: 'Underfloor (gas)',
    }, {
        title: 'Underfloor (solar)',
        value: 'Underfloor (solar)',
    }, {
        title: 'Unknown',
        value:'Unknown',
    }, {
        title: 'Wood burner',
        value: 'Wood burner',
    },
  ],
}, {
  id: 'cookingType',
  title: 'Cooking Type',
  component: 'select',
  options: [{
        title: 'Please select...',
        value: '',
    }, {
        title: 'AGA',
        value: 'AGA',
    }, {
        title: 'Combination of Gas and Electric',
        value: 'Combination of Gas and Electric',
    }, {
        title: 'Electricity',
        value: 'Electricity',
    }, {
        title: 'Gas',
        value: 'Gas',
    }, {
        title: 'Heat pump',
        value: 'Heat',
    }, {
        title: 'Other',
        value: 'Other',
    }, {
        title: 'Shared/municipal',
        value: 'Shared/municipal',
    }, {
        title: 'Solar',
        value: 'Solar',
    }, {
        title: 'Unknown',
        value: 'Unknown',
    },
  ],
}, {
  id: 'depthLoftInsulation',
  title: 'Depth of loft insulation (mm)',
}, {
  id: 'openFireplaces',
  title: 'Number of open fireplaces',
}, {
  id: 'doubleGlazingPercentage',
  title: 'Amount of double glazing (%)',
}, {
  id: 'numberOccupants',
  title: 'Number of occupants',
}].concat(energyUsageSchema).concat([
  {
    id: 'whereHeard',
    title: 'Where did you hear about us?',
    component: 'heading',
    cosmetic: true,
  },
  {
    id: 'howHearSurvey',
    title: 'How did you hear about thermal-imaging surveys?',
    component: 'select',
    // Facebook, LinkedIn, X, Word of Mouth, Neighbourhood WhatsApp, Community/Climate event, Networking, Instagram, Website, Google Search, Flyer through your door, Local newspaper or magazine.  Other
    options: [{
          title: 'Please select...',
          value: '',
      }, {
          title: 'Facebook',
          value: 'Facebook',
      }, {
          title: 'LinkedIn',
          value: 'LinkedIn',
      }, {
          title: 'X',
          value: 'X',
      }, {
          title: 'Word of Mouth',
          value: 'Word of Mouth',
      }, {
          title: 'Neighbourhood WhatsApp',
          value: 'Neighbourhood WhatsApp',
      }, {
          title: 'Community/Climate event',
          value: 'Community/Climate event',
      }, {
          title: 'Networking',
          value: 'Networking',
      }, {
          title: 'Instagram',
          value: 'Instagram',
      }, {
          title: 'Website',
          value: 'Website',
      }, {
          title: 'Google Search',
          value: 'Google Search',
      }, {
          title: 'Flyer through your door',
          value: 'Flyer through your door',
      }, {
          title: 'Local newspaper or magazine',
          value: 'Local newspaper or magazine',
      }, {
          title: 'Other',
          value: 'Other',
      },
    ],
  }] as any) as FormSchema

export const presurveyValidation = yup.object().shape({
  homeDetails: yup.string().required(),
})

/*

  POST SURVEY

*/
export const postsurveySchema = [{
  id: 'section1',
  title: 'Survey Details',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'externalTemperature',
  title: 'External Temperature (°C)',
}, {
  id: 'cameraKitNumber',
  title: 'Camera Kit Number',
}, {
  id: 'section2',
  title: 'Findings',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'faultsIdentified',
  title: 'Faults Identified',
  component: 'textarea'
}, {
  id: 'recommendations',
  title: 'Recommendations',
  component: 'textarea'
}, {
  id: 'notes',
  title: 'Notes',
  component: 'textarea'
}] as FormSchema

export const postsurveyValidation = yup.object().shape({
  
})


export const RADIO_OPTIONS = [{
  title: '1',
  value: '1',
}, {
  title: '2',
  value: '2',
}, {
  title: '3',
  value: '3',
}, {
  title: '4',
  value: '4',
}, {
  title: '5',
  value: '5',
}]
/*

  MONTH FEEDBACK

*/
export const monthfeedbackSchema: FormSchema = [{
  id: 'section1',
  title: 'One month feedback',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'specialRequirementsHelperList',
  component: 'html',
  helperText: `
<p>As part of the CHEESE survey process, we check in with householders one month and one year after the survey. After one month, we would like to know what you are thinking of doing to do reduce your energy consumption, as well as any feedback your have. This data is important for us to demonstrate the effectivness of what we do.</p>
<p>Please complete the form below to provide us with your one-month feedback.</p>
`,
  cosmetic: true,
}, {
  id: 'section1',
  title: 'Remedial work',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'takenAction',
  title: 'Have you already taken action to improve the thermal efficiency of your home?',
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'takenActionNotes',
  title: 'If so, then what have you done?',
  helperText: 'And have you done the work yourself or has it been done professionally?',
  component: 'textarea'
}, {
  id: 'willTakeAction',
  title: 'Are you planning to do remedial work in the next few years?',
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'willTakeActionNotes',
  title: 'If so, then what are you planning?',
  helperText: 'This can be anything from draught proofing to installing external wall insulation.',
  component: 'textarea'
}, {
  id: 'section2',
  title: 'Your wellbeing',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'actionsHouseWarmer',
  title: `Have the actions you've taken made your house feel warmer?`,
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'actionsHouseWarmerNotes',
  title: 'If so, then how?',
  helperText: `(Even if perhaps you haven't saved money on your bills.)`,
  component: 'textarea'
}, {
  id: 'section3',
  title: 'Changes in behaviour',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'changeBehaviour',
  title: `Has your behaviour changed as a result of your survey?`,
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'changeBehaviourTemperature',
  title: 'How has the period and temperature you use the heating for changed? (if at all)',
  component: 'textarea'
}, {
  id: 'changeBehaviourSpace',
  title: 'How do you use space in your home differently? (if at all)',
  component: 'textarea'
}, {
  id: 'changeBehaviourOther',
  title: 'In what other ways has your behaviour changed after the survey?',
  component: 'textarea'
}, {
  id: 'section4',
  title: 'Feedback to us',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'feedbackSatisfied',
  title: `How satisified were you with the survey overall?`,
  helperText: '(1: least, to 5: most)',
  component: 'radio',
  options: RADIO_OPTIONS,
  meta: {
    row: true,
  }
}, {
  id: 'feedbackVideo',
  title: `How useful have you find the survey video?`,
  helperText: '(1: least, to 5: most)',
  component: 'radio',
  options: RADIO_OPTIONS,
  meta: {
    row: true,
  }
}, {
  id: 'feedbackConduct',
  title: `How was the conduct of the surveyor?`,
  helperText: '(1: least, to 5: most)',
  component: 'radio',
  options: RADIO_OPTIONS,
  meta: {
    row: true,
  }
}, {
  id: 'feedbackValue',
  title: `Was the survey good value for money?`,
  helperText: '(1: least, to 5: most)',
  component: 'radio',
  options: RADIO_OPTIONS,
  meta: {
    row: true,
  }
}, {
  id: 'feedbackRecommend',
  title: `Are you likely to recommend the survey to a friend or neighbour?`,
  helperText: '(1: least, to 5: most)',
  component: 'radio',
  options: RADIO_OPTIONS,
  meta: {
    row: true,
  }
}, {
  id: 'feedbackGeneral',
  title: 'Do you have any feedback?',
  helperText: 'We would like to hear what you think about: the organisation of the survey, the conduct of the Heatview Surveyors, the results of the survey and suggested remedies, the overall value for money of the survey, your overall satisfaction, and anything else at all you would like to let us know.',
  component: 'textarea'
}].concat(energyUsageSchema) as FormSchema

export const monthfeedbackValidation = yup.object().shape({
  
})

/*

  YEAR FEEDBACK

*/
export const yearfeedbackSchema = [{
  id: 'section1',
  title: 'One year feedback',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'specialRequirementsHelperList',
  component: 'html',
  helperText: `
<p>As part of the CHEESE survey process, we check in with householders one month and one year after the survey. After one year we would we would like to find out how you have got on and what effect the changes have had. This data is important for us to demonstrate the effectivness of what we do.</p>
<p>Please complete the form below to provide us with your one-year feedback.</p>
`,
  cosmetic: true,
}, {
  id: 'section1',
  title: 'Remedial work',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'takenAction',
  title: 'Have you taken action since your survey to improve the thermal efficiency of your home?',
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'takenActionWorkYourself',
  title: 'What work have you done yourself?',
  component: 'textarea'
}, {
  id: 'takenActionWorkProfessionally',
  title: 'And, what work, if any, have you paid for to be done professionally?',
  component: 'textarea'
}, {
  id: 'takenActionWorkContractors',
  title: 'If you had work done professionally, which contractors did you use?',
  helperText: 'And were these contractors based in your city or from further afield?',
  component: 'textarea'
}, {
  id: 'takenActionCosts',
  title: 'Approximately how much have you spent in total on energy improvements to your home? (£)',
  helperText: 'Only answer this if you feel comfortable to.',
}, {
  id: 'takenActionCostsDIY',
  title: 'Approximately how much did you spend on DIY? (£)',
}, {
  id: 'takenActionCostsContractors',
  title: 'Approximately how much did you spend on local contractors? (£)',
}, {
  id: 'takenActionCostsLowIncome',
  title: 'Other Help?',
  helperText: 'If you are a low-income household or do not own your home, have you asked the council, your housing association or your landlord to make any changes to your home following the survey? If so, what was their response?',
  component: 'textarea'
},{
  id: 'section3',
  title: 'Future remedial work',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'remedialWorkPlanned',
  title: ` Do you have any future work planned?`,
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'remedialWorkPlannedNotes',
  title: 'If so, what are you planning?',
  component: 'textarea'
}, {
  id: 'section4',
  title: 'Changes in behaviour',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'changeBehaviour',
  title: `Has your behaviour changed as a result of your survey?`,
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'changeBehaviourTemperature',
  title: 'How has the period and temperature you use the heating for changed? (if at all)',
  component: 'textarea'
}, {
  id: 'changeBehaviourSpace',
  title: 'How do you use space in your home differently? (if at all)',
  component: 'textarea'
}, {
  id: 'changeBehaviourOther',
  title: 'In what other ways has your behaviour changed after the survey?',
  component: 'textarea'
}, {
  id: 'energyUsage',
  title: 'Energy Usage',
  component: 'heading',
  cosmetic: true,
}].concat(energyUsageSchema).concat([{
  id: 'wellBeing',
  title: 'Your wellbeing',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'actionsHouseWarmer',
  title: `Have the actions you've taken made your house feel warmer?`,
  component: 'radio',
  options: [{
    title: 'Yes',
    value: 'yes',
  }, {
    title: 'No',
    value: 'no'
  }],
  meta: {
    row: true,
  }
}, {
  id: 'actionsHouseWarmerNotes',
  title: 'If so, then how?',
  helperText: `(Even if perhaps you haven't saved money on your bills.)`,
  component: 'textarea'
},{
  id: 'grantFunders',
  title: 'For our grant-funders',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'healthChanges',
  title: 'Has there been any change to the health of members of your household as a result of remedial work following the survey?',
  component: 'textarea'
}, {
  id: 'reasonForNotDoingWork',
  title: `If you haven't done any rectification work, was there a particular reason?`,
  component: 'radio',
  options: [{
    title: 'Lack of funds',
    value: 'lackOfFunds',
  }, {
    title: 'Lack of knowledge/skill',
    value: 'lackOfKnowledge',
  }, {
    title: 'Lack of confidence',
    value: 'lackOfConfidence',
  }, {
    title: 'Lack of time',
    value: 'lackOfTime',
  }, {
    title: 'Personal situation',
    value: 'personalSituation',
  }, {
    title: 'Tenure situation (e.g. landlord not allowing changes)',
    value: 'tenureSituation',
  }],
  meta: {
    row: false,
  }
}, {
  id: 'retrofitWorkPreference',
  title: `If you would consider starting retrofit work, what would be your preference on starting (if there were no barriers)?`,
  component: 'radio',
  options: [{
    title: 'Do it yourself for low-cost Interventions',
    value: 'diy',
  }, {
    title: 'Contract a handyperson for specific work',
    value: 'contract',
  }, {
    title: 'instruct a company to manage full upgrade',
    value: 'company',
  }],
  meta: {
    row: false,
  }
}, {
  id: 'retrofitWorkServices',
  title: `Would any of the below services make you more likely to have retrofit work done?`,
  component: 'multipleCheckbox',
  options: [{
    title: 'Online demonstration videos',
    value: 'onlineDemonstrationVideos',
  }, {
    title: 'Online guidance',
    value: 'onlineGuidance',
  }, {
    title: 'In-person workshop to demonstrate retrofitting work',
    value: 'inPerson',
  }, {
    title: 'Online guidance',
    value: 'onlineGuidance',
  }, {
    title: 'Easy access to grant funding',
    value: 'grantFunding',
  }, {
    title: 'Easy access loans',
    value: 'accessLoans',
  }, {
    title: 'Access to skilled service providers for you to contract',
    value: 'serviceProviders',
  }],
  meta: {
    row: false,
  }
}, {
  id: 'heardOfOrganisations',
  title: `Have you heard of any of the following organisations?`,
  component: 'multipleCheckbox',
  options: [{
    title: 'Age UK Handyperson Services',
    value: 'ageUK',
  }, {
    title: 'Bristol Energy Network',
    value: 'bristolEnergyNetwork',
  }, {
    title: 'Centre for Sustainable Energy',
    value: 'centreForSyustainableEnergy',
  }, {
    title: 'City Leap',
    value: 'cityLeap',
  }, {
    title: 'Feeding Bristol',
    value: 'feedingBristol',
  }, {
    title: 'Great Western Credit Union',
    value: 'greatWesternCreditUnion',
  }, {
    title: 'Lendology',
    value: 'lenology',
  }, {
    title: 'Retrofit West',
    value: 'retroFitWest',
  }, {
    title: 'Re:work',
    value: 'reWork',
  }, {
    title: 'Shelter',
    value: 'shelter',
  }, {
    title: 'Talking Money',
    value: 'talkingMoney',
  }, {
    title: 'The Green Register',
    value: 'theGreenRegister',
  }, {
    title: 'We Care Home Improvements',
    value: 'weCareHomeImprovements',
  }, {
    title: 'Welcoming Spaces',
    value: 'welcomingSpaces',
  }, {
    title: 'WHAM',
    value: 'wham',
  }],
  meta: {
    row: false,
  }
}, {
  id: 'section5',
  title: 'Feedback to us',
  component: 'heading',
  cosmetic: true,
}, {
  id: 'feedbackGeneral',
  title: 'Lastly, do you have any other feedback on the CHEESE Project?',
  helperText: 'We would like to hear what you think about: how useful the survey was, your overall satisfaction, and anything else at all you would like to let us know.',
  component: 'textarea'
}]) as FormSchema

export const yearfeedbackValidation = yup.object().shape({

})

/*

  PRICING

*/
export const productSchema: FormSchema = [{
  id: 'name',
  title: 'Name',
  //helperText: 'Enter the name of the product (e.g. 2 rooms)',
}, {
  id: 'length',
  title: 'Length (mins)',
  //helperText: 'Enter the length in minutes the booking will take (e.g. 120 for 2 hours)',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'price',
  title: 'Price (pounds)',
  //helperText: 'Enter the price of the booking in whole pounds (e.g. 260)',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'comment',
  title: 'Comment',
  component: 'textarea',
}]

export const productValidation = yup.object().shape({
  name: yup.string().required(),
  length: yup.number().integer().required(),
  price: yup.number().integer().required(),
})

export const productValues = {
  name: '',
  length: 120,
  price: 120,
}

/*

  GENERAL CONFIG

*/
export const generalConfigSchema: FormSchema = [{
  id: 'new_booking_email_template',
  title: 'New Booking Email Template',
  helperText: 'What email template to use for new bookings',
  component: 'astonish_email_template_select',
}, {
  id: 'updated_booking_email_template',
  title: 'Updated Booking Email Template',
  helperText: 'What email template to use for updated bookings',
  component: 'astonish_email_template_select',
}, {
  id: 'cancelled_booking_email_template',
  title: 'Cancalled Booking Email Template',
  helperText: 'What email template to use for cancelled bookings',
  component: 'astonish_email_template_select',
}, {
  id: 'admin_booking_email_template',
  title: 'Admin Booking Email Template',
  helperText: 'What email template to use for surveyors and admins',
  component: 'astonish_email_template_select',
}, {
  id: 'coverage_outcodes',
  title: 'Coverage Outcodes',
  helperText: 'Outcodes covered by this franchise - e.g. (BS1, BS2, BS3) - comma separated',
  component: 'textarea',
}, {
  id: 'travel_time_before',
  title: 'Travel Time Before (mins)',
  helperText: 'How many minutes for travel before a booking',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'travel_time_after',
  title: 'Travel Time After (mins)',
  helperText: 'How many minutes for travel after a booking',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'slot_interval',
  title: 'Slot Interval (mins)',
  helperText: 'How many minutes between offered slots',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'buffer_zone',
  title: 'Buffer Zone (days)',
  helperText: 'How many days from today are bookings disabled (to prevent bookings being made for e.g. tomorrow)',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'cutoff_zone',
  title: 'Cut Off Zone (days)',
  helperText: 'How many days from today are bookings allowed (to prevent bookings being made too far into the future)',
  inputProps: {
    type: 'number',
  }
}, {
  id: 'contact_email',
  title: 'Contact Email',
  helperText: 'The email address people should use to make bookings manaully',
}, {
  id: 'contact_phone',
  title: 'Contact Phone',
  helperText: 'The phone number people should use to make bookings manaully',
}, {
  id: 'welcome_text',
  title: 'Welcome Text',
  component: 'layout',
}, {
  id: 'introduction',
  title: 'Booking Form Introduction',
  component: 'layout',
}, {
  id: 'conditions_preamble',
  title: 'Conditions Preamble',
  component: 'layout',
}, {
  id: 'covid_precautions_text_preamble',
  title: 'Covid Precautions Text (preamble)',
  component: 'layout',
}, {
  id: 'covid_precautions_text',
  title: 'Covid Precautions Text (checkbox)',
  component: 'layout',
}, {
  id: 'conditions_prep_text_preamble',
  title: 'Conditions Prep Text (preamble)',
  component: 'layout',
}, {
  id: 'conditions_prep_text',
  title: 'Conditions Prep Text (checkbox)',
  component: 'layout',
}, {
  id: 'conditions_photos_text_preamble',
  title: 'Conditions Photos Text (preamble)',
  component: 'layout',
}, {
  id: 'conditions_photos_text',
  title: 'Conditions Photos Text (checkbox)',
  component: 'layout',
}, {
  id: 'conditions_cancel_text_preamble',
  title: 'Conditions Cancel Text (preamble)',
  component: 'layout',
}, {
  id: 'conditions_cancel_text',
  title: 'Conditions Cancel Text (checkbox)',
  component: 'layout',
}, {
  id: 'conditions_footer',
  title: 'Conditions Footer',
  component: 'layout',
}, {
  id: 'pricing_header',
  title: 'Pricing Header',
  component: 'layout',
}, {
  id: 'pricing_footer',
  title: 'Pricing Footer',
  component: 'layout',
}, {
  id: 'confirmation_header',
  title: 'Confirmation Header',
  component: 'layout',
}, {
  id: 'confirmation_footer',
  title: 'Confirmation Footer',
  component: 'layout',
}]

export const generalConfigValidationSchema = yup.object().shape({
  travel_time_before: yup.number().integer().required(),
  travel_time_after: yup.number().integer().required(),
  coverage_outcodes: (yup.string() as any).coverageOutcodes('Invalid Outcode'),
})

/*

  ALL

*/

const requiredCrmSchema = mapSchema(crmSchema, item => {
  if(item.id == 'firstname' || item.id == 'email' || item.id == 'address1') {
    return Object.assign({}, item, {
      title: item.title + ' (*)'
    })
  }
  return item
})

// we need to lock down the postcode field for the public form
const publicCrmSchema = mapSchema(requiredCrmSchema, item => {
  if(item.id == 'postcode') {
    return Object.assign({}, item, {
      helperText: 'You cannot change your postcode because it decides which surveyor you are assigned to. If you need to change your postcode, please restart your booking.',
    })
  }
  return item
})

export const crmSchemaAll = prependFormSchema(requiredCrmSchema, 'crm.')
export const crmSchemaAllPublic = prependFormSchema(publicCrmSchema, 'crm.')
export const prebookingSchemaAll = prependFormSchema(prebookingSchema, 'prebooking.')
export const presurveySchemaAll = prependFormSchema(presurveySchema, 'presurvey.')
export const postsurveySchemaAll = prependFormSchema(postsurveySchema, 'postsurvey.')
export const monthfeedbackSchemaAll = prependFormSchema(monthfeedbackSchema, 'monthfeedback.')
export const yearfeedbackSchemaAll = prependFormSchema(yearfeedbackSchema, 'yearfeedback.')
export const productSchemaAll = prependFormSchema(productSchema, 'product.')

export const allValidation = yup.object().shape({
  crm: crmValidation,
  // don't validate here otherwise admin has to put all values
  // presurvey: presurveyValidation,
  // postsurvey: postsurveyValidation,
  // monthfeedback: monthfeedbackValidation,
  // yearfeedback: yearfeedbackValidation,
  product: productValidation,
  assigned_et: yup.string().required(),
})

export const allValues: IRecord_Booking_Core_Editable = {
  reference: '',
  crm: crmValues,
  prebooking: Object.assign({}, getInitialValues(prebookingSchema, {}), {
    covid_precautions: false,
    conditions_photos: false,
    conditions_prep: false,
    conditions_cancel: false,
  }),
  presurvey: getInitialValues(presurveySchema, {}),
  postsurvey: getInitialValues(postsurveySchema, {}),
  monthfeedback: getInitialValues(monthfeedbackSchema, {}),
  yearfeedback: getInitialValues(yearfeedbackSchema, {}),
  assigned_et: '',
  date: '',
  state: BOOKING_STATE_CONFIRMED,
  product: productValues,
  payments: [],
  tags: [],
  notes: '',
}
